import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/layout/MainLayout';
import EmailSmtpSetup from './EmailSmtpSetup';
import LeadSelectTable from '../../../components/automation/LeadSelectTable';
import MediaPickerModal from '../../../components/shared/MediaPickerModal';
import { Paperclip, Plus, X } from 'lucide-react';
import api from '../../../utils/api';

const EmailCampaignWizard = () => {
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api('/email-templates');
        if (res.ok) setTemplates(await res.json());
      } catch (err) {
        console.error(err);
      }
    };
    fetchTemplates();
  }, []);

  const handleTemplateSelect = (e) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    if (!id) return;
    const t = templates.find(temp => temp._id === id);
    if (t) {
      setSubject(t.subject);
      setBody(t.body);
      setAttachments(t.attachments || []);
    }
  };

  const handleAttachFile = (file) => {
    if (attachments.some(a => a.fileId === file._id)) return;
    setAttachments([...attachments, { fileId: file._id, fileName: file.fileName, fileUrl: file.fileUrl }]);
  };

  const handleRemoveAttachment = (fileId) => {
    setAttachments(attachments.filter(a => a.fileId !== fileId));
  };

  const handleSend = async () => {
    if (selectedLeads.length === 0) return alert('Select at least one lead');
    if (!subject.trim()) return alert('Please enter a subject');
    if (!body.trim()) return alert('Please enter an email body');

    setSending(true);
    try {
      const res = await api('/email-automation/send', {
        method: 'POST',
        body: {
          leadIds: selectedLeads,
          subject,
          body,
          attachments
        }
      });
      const data = await res.json();
      
      if (res.ok && data.success) {
        setResult(data.campaign);
      } else {
        alert(data.message || 'Failed to send campaign');
      }
    } catch (err) {
      alert(err.message || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        <button 
          onClick={() => navigate('/automation')} 
          style={{ marginBottom: '16px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
        >
          ← Back to Automation Hub
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>Send Cold Emails</h1>

        <EmailSmtpSetup onStatusChange={setSmtpConfigured} />

        {result ? (
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-primary)' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#16a34a', marginBottom: '16px' }}>✅ Campaign Completed</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{result.stats.totalSelected}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Total Selected</div>
              </div>
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{result.stats.hasEmail}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Has Email</div>
              </div>
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>{result.stats.skipped}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Skipped (No Email)</div>
              </div>
              <div style={{ background: '#dcfce7', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a' }}>{result.stats.sent}</div>
                <div style={{ fontSize: '12px', color: '#166534' }}>Sent</div>
              </div>
              <div style={{ background: '#fee2e2', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>{result.stats.failed}</div>
                <div style={{ fontSize: '12px', color: '#991b1b' }}>Failed</div>
              </div>
            </div>

            <button 
              onClick={() => navigate(`/automation/email/history/${result._id}`)}
              style={{ padding: '10px 20px', background: 'var(--color-brand-primary)', color: 'white', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
            >
              View Detailed Results in History
            </button>
          </div>
        ) : (
          <>
            <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-primary)', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>1. Select Leads</h2>
              <LeadSelectTable selectedLeads={selectedLeads} setSelectedLeads={setSelectedLeads} />
            </div>

            <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-primary)', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>2. Compose Email</h2>
                <select 
                  value={selectedTemplateId}
                  onChange={handleTemplateSelect}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border-primary)', fontSize: '14px', outline: 'none' }}
                >
                  <option value="">-- Load a Template (Optional) --</option>
                  {templates.map(t => (
                    <option key={t._id} value={t._id}>{t.name}</option>
                  ))}
                </select>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Subject</label>
                <input 
                  type="text" 
                  value={subject} 
                  onChange={(e) => setSubject(e.target.value)} 
                  placeholder="e.g. Special Offer for {{lead_name}}"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--color-border-primary)' }} 
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Body (Plain Text)</label>
                <textarea 
                  value={body} 
                  onChange={(e) => setBody(e.target.value)} 
                  rows={8}
                  placeholder="Hi {{lead_name}},\n\nWe have a special offer for you...\n\nBest,\nYour Team"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--color-border-primary)', fontFamily: 'inherit', resize: 'vertical', marginBottom: '8px' }} 
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Attachments</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {attachments.map(att => (
                    <div key={att.fileId} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#F1F5F9', padding: '6px 10px', borderRadius: '4px', fontSize: '13px', color: '#334155', border: '1px solid #E2E8F0' }}>
                      <Paperclip size={14} />
                      <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.fileName}</span>
                      <button onClick={() => handleRemoveAttachment(att.fileId)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#94A3B8' }}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <button 
                    onClick={() => setShowMediaPicker(true)}
                    style={{ background: 'white', border: '1px dashed var(--color-brand-primary)', color: 'var(--color-brand-primary)', padding: '6px 12px', borderRadius: '4px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Plus size={16} /> Add Attachment
                  </button>
                </div>
              </div>

              <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                Supported variables: <code>{'{{lead_name}}'}</code>, <code>{'{{lead_phone}}'}</code>, <code>{'{{lead_email}}'}</code> and any custom fields (e.g. <code>{'{{company}}'}</code>)
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                onClick={handleSend}
                disabled={!smtpConfigured || sending || selectedLeads.length === 0}
                style={{ 
                  padding: '14px 28px', 
                  background: 'var(--color-brand-primary)', 
                  color: 'white', 
                  borderRadius: '8px', 
                  fontWeight: 'bold', 
                  cursor: (!smtpConfigured || sending || selectedLeads.length === 0) ? 'not-allowed' : 'pointer', 
                  border: 'none', 
                  fontSize: '16px',
                  opacity: (!smtpConfigured || sending || selectedLeads.length === 0) ? 0.6 : 1
                }}
              >
                {sending ? 'Sending Campaign...' : `Send to ${selectedLeads.length} Leads`}
              </button>
            </div>
          </>
        )}
      </div>

      <MediaPickerModal 
        isOpen={showMediaPicker} 
        onClose={() => setShowMediaPicker(false)} 
        onSelect={handleAttachFile} 
      />
    </MainLayout>
  );
};

export default EmailCampaignWizard;
